import { ValidatorFn } from '@angular/forms';
import { DateFormatType, FileFolderType, IFormInputMask, TimeFormatType } from '@shared/core';

export enum FormFieldType {
  GROUP = 'group',
  INPUT = 'input',
  PASSWORD = 'password',
  TEXTAREA = 'textarea',
  CHECKBOX = 'checkbox',
  SELECT = 'select',
  DATE = 'date',
  TIME = 'time',
  FILE = 'file',
  TEXT_EDITOR = 'text-editor',
  TIMEPICKER = 'timepicker',
}

export const enum FormGroupDirectionType {
  ROW = 'row',
  COLUMN = 'column',
}

export interface IFormHideCondition {
  fieldName: string;
  condition?: (fieldValue?: any) => boolean;
}

export interface IFormGroupConfig {
  type: FormFieldType.GROUP;
  label?: string;
  direction?: FormGroupDirectionType;
  fields?: IFormGroupFieldConfig | IFormGroupConfig;
  forWrapping?: boolean;
  hidden?: boolean;
  hideCondition?: IFormHideCondition[];
  validators?: ValidatorFn[];
}

export interface IFormGroupFieldConfig {
  [key: string]: FormGroupFieldConfigType;
}

export type FormGroupFieldConfigType = FormFieldConfigType | IFormGroupConfig;
export type FormFieldConfigType =
  | IFormFieldInputConfig
  | IFormFieldPasswordConfig
  | IFormFieldCheckboxConfig
  | IFormFieldTextareaField
  | FormFieldSelectConfigType
  | IFormFieldDateConfig
  | IFormFieldTimeConfig
  | IFormFieldFileConfig
  | IFormFieldTextEditorConfig
  | IFormFieldTimePickerConfig;

export interface IFormFieldConfig {
  type: FormFieldType;
  label?: string;
  link?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  value?: any;
  validators?: ValidatorFn[];
  hidden?: boolean;
  hideCondition?: IFormHideCondition[];
  translatable?: boolean;
  hideLabel?: boolean;
  hideError?: boolean;
  loading?: boolean;
}

export interface IFormFieldInputConfig extends IFormFieldConfig {
  type: FormFieldType.INPUT;
  maskConfig?: IFormInputMask; // ngx-mask @link https://github.com/JsDaddy/ngx-mask/blob/develop/USAGE.md
  isGrouped?: boolean;
  isUpperCase?: boolean;
  suffixIcon?: string;
}

export interface IFormFieldPasswordConfig extends IFormFieldConfig {
  type: FormFieldType.PASSWORD;
}

export interface IFormFieldCheckboxConfig extends IFormFieldConfig {
  type: FormFieldType.CHECKBOX;
  multiple?: boolean;
  requestUrl?: string;
  options?: {
    labelField: string;
    valueField: string;
  };
}

export interface IFormFieldDateConfig extends IFormFieldConfig {
  type: FormFieldType.DATE;
  mode?: FormFieldDateModeType;
  showTime?: string;
  format?: DateFormatType;
  maxDateOrCurrent?: Date | boolean; // If true, the current date, otherwise the date
  minDateOrCurrent?: Date | boolean;
}

export type FormFieldDateModeType = 'date' | 'week' | 'month' | 'year';

export interface IFormFieldTimeConfig extends IFormFieldConfig {
  type: FormFieldType.TIME;
  format?: TimeFormatType;
}

export interface IFormFieldTimePickerConfig extends IFormFieldConfig {
  type: FormFieldType.TIMEPICKER;
  isDurationTime?: boolean;
  format?: TimeFormatType;
}

export type FormFieldSelectConfigType = IFormFieldSelectConfig | IFormFieldServerSelectConfig;

export interface IFormFieldSelectBaseConfig extends IFormFieldConfig {
  type: FormFieldType.SELECT;
  optionLabelField?: string;
  optionValueField?: string;
  showSearch?: boolean;
  customOptionFields?: string[];
  multiple?: boolean;
  maxCount?: number;
  optionIconField?: string;
}

export interface IFormFieldSelectConfig extends IFormFieldSelectBaseConfig {
  options: any[];
}
export interface IFormFieldServerSelectConfig extends IFormFieldSelectBaseConfig {
  requestUrl: string;
  requestMethod?: string;
  requestParams?: IFormFieldServerSelectRequestParamsConfig;
  pageable?: boolean;
  paginationType?: IFormFieldServerSelectPaginationType;
  searchField?: string;
  responseMapper?: (response: any) => any;
}

export type IFormFieldServerSelectPaginationType = 'offset' | 'page';

export interface IFormFieldServerSelectRequestParamsConfig {
  [paramName: string]: {
    relatedFieldName: string;
    required?: boolean;
    valueMapper?: (relatedFieldValue: any) => any;
  };
}

export interface IFormFieldTextareaField extends IFormFieldConfig {
  type: FormFieldType.TEXTAREA;
  minRows?: number;
  maxRows?: number;
}

export interface IFormFieldFileConfig extends IFormFieldConfig {
  type: FormFieldType.FILE;
  folderType: FileFolderType;
  removeFromServer?: boolean;
}

export interface IFormFieldTextEditorConfig extends IFormFieldConfig {
  type: FormFieldType.TEXT_EDITOR;
  maxLength?: number;
  minLength?: number;
  editorConfig: {
    paramsFieldName?: string;
    paramsMapper?: (
      item: any,
      index: number,
    ) => {
      id: string;
      value: string;
      [key: string]: string | undefined;
    };
    rows?: number;
    newLine?: boolean;
    toolbar?: boolean;
    readonly?: boolean;
    withoutHtml?: boolean;
  };
}

export interface TimePickerValue {
  hour: number;
  minute: number;
}
